import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import {
  FinalCta,
  HEADLINE_FROM,
  HEADLINE_TO,
  HeroLights,
  makeReveal,
  playWhenSeen,
  useSectionReveal,
  zoomOutOnScroll,
} from '@/components/marketing';
import { Glow, Icon, LinkButton } from '@/components/ui';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

gsap.registerPlugin(ScrollTrigger);

/**
 * Placeholder openings for the hero's feed, three columns of three. None of
 * these companies exist; swap for `internshipApi` results before launch.
 */
const FEED = [
  [
    {
      company: 'Nova AI',
      role: 'Product Designer Intern',
      place: 'Amsterdam',
      pay: '$1.200/mo',
      tags: ['Figma', 'AI'],
    },
    {
      company: 'Cobalt',
      role: 'Data Analyst Intern',
      place: 'Paris',
      pay: '$1.500/mo',
      tags: ['SQL', 'dbt'],
    },
    {
      company: 'Kite',
      role: 'Brand Design Intern',
      place: 'Milan',
      pay: '$1.150/mo',
      tags: ['Motion', 'Type'],
    },
  ],
  [
    {
      company: 'Orbit',
      role: 'ML Engineering Intern',
      place: 'London',
      pay: '$1.800/mo',
      tags: ['Python', 'LLMs'],
    },
    {
      company: 'Drift',
      role: 'Frontend Intern',
      place: 'Lisbon',
      pay: '$1.300/mo',
      tags: ['React', 'TS'],
    },
    {
      company: 'Forge',
      role: 'Backend Intern',
      place: 'Dublin',
      pay: '$1.600/mo',
      tags: ['Go', 'SQL'],
    },
  ],
  [
    {
      company: 'Lumen',
      role: 'Growth Marketing Intern',
      place: 'Berlin',
      pay: '$1.400/mo',
      tags: ['SEO', 'Ads'],
    },
    {
      company: 'Halo',
      role: 'UX Research Intern',
      place: 'Madrid',
      pay: '$1.250/mo',
      tags: ['Figma', 'Users'],
    },
    {
      company: 'Vera',
      role: 'Product Ops Intern',
      place: 'Zurich',
      pay: '$1.700/mo',
      tags: ['Ops', 'Data'],
    },
  ],
];

const STEPS = [
  {
    title: 'Create your profile',
    body: 'Showcase your skills, experience, interests, and what you’re looking for.',
  },
  {
    title: 'Discover your match',
    body: 'Browse internships or get matched with opportunities curated around your profile and goals.',
  },
  {
    title: 'Connect & get started',
    body: 'Apply, review, and connect to move forward.',
  },
];

export function StudentsPage() {
  return (
    <>
      <Hero />
      <HowItWorks />
      <FinalCta />
    </>
  );
}

/**
 * "Begin your journey. Build your career."
 *
 * Copy on the left; on the right, a feed of openings that never stops moving.
 * Each column is its cards three times over, slid up by exactly a third of
 * its height on a linear loop -- the second copy arrives where the first
 * began, so the restart is invisible, and three copies rather than two keeps
 * the column taller than its box at every size. The motion is CSS, off the main thread; GSAP only
 * fades the whole feed in on arrival. A mask, not an overlay, does the fade
 * at the top and foot: it takes the cards down into whatever is behind them
 * rather than painting a colour over the top.
 */
function Hero() {
  const root = useSectionReveal(
    ({ reveal }) => {
      reveal('.js-hero-line', HEADLINE_FROM, { ...HEADLINE_TO, duration: 2.2 }, 0.1);
      reveal(
        '.js-hero-copy',
        { y: 24, opacity: 0 },
        { duration: 0.9, stagger: 0.12, ease: 'power3.out' },
        1.1,
      );
      reveal('.js-feed', { y: 18, opacity: 0 }, { duration: 0.8, ease: 'power2.out' }, 0.3);
    },
    { start: 'top 80%', once: true },
  );

  return (
    <section
      ref={root}
      className="relative isolate flex min-h-dvh flex-col justify-center overflow-hidden pt-28 pb-16 lg:pt-32"
    >
      <HeroLights />

      {/* A plain wrapper: the measure is `mx-auto` with `width: auto`, and as
          a flex item that shrinks to its content. */}
      <div>
        <div className="wonder-container wonder-measure relative grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-10">
          <div>
            <h1 className="font-tight text-[clamp(1.75rem,3.125vw,3.75rem)] leading-[1.292] font-semibold tracking-[-0.0253em] uppercase">
              <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
                <span className="js-hero-line text-on-night block">Begin your journey.</span>
              </span>{' '}
              <span className="block overflow-hidden pb-[0.08em] mb-[-0.08em]">
                <span className="js-hero-line text-iris-300 block">Build your career.</span>
              </span>
            </h1>
            {/* Inspector: Inter 400, 20 on 28, white at 80%. */}
            <p className="js-hero-copy mt-6 text-xl leading-7 text-white/80">
              Wonder today, Work tomorrow.
            </p>
            <div className="js-hero-copy mt-8">
              <LinkButton
                to={paths.registerStudent}
                variant="light"
                shape="pill"
                size="pillMd"
                className="border border-[#CDACF6]"
              >
                Set Up Your Profile &mdash; It&rsquo;s Free
                <Icon name="arrowRight" size={16} />
              </LinkButton>
            </div>
          </div>

          <Feed />
        </div>
      </div>
    </section>
  );
}

function Feed() {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'js-feed group/feed relative h-104 overflow-hidden lg:h-120',
        'mask-[linear-gradient(180deg,transparent_0%,#000_22%,#000_72%,transparent_100%)]',
      )}
    >
      {/* Three 178px columns, as the frame sets them, kept to the right of
          the half they sit in. */}
      <div className="ml-auto grid max-w-146 grid-cols-3 gap-4">
        {FEED.map((column, index) => (
          <div
            key={index}
            // The first column starts half a card higher, so the three
            // columns read as a staggered wall rather than a table, and the
            // middle one runs the loop backwards -- down while its neighbours
            // go up. Reversed, the same keyframes are still seamless: the
            // column starts a copy up and comes home.
            className={cn(
              'flex flex-col gap-4 will-change-transform',
              'animate-[wonder-feed_10s_linear_infinite] motion-reduce:animate-none max-lg:animate-[wonder-feed_16s_linear_infinite]',
              'group-hover/feed:[animation-play-state:paused]',
              index === 0 && '-mt-16',
              index === 1 && '[animation-direction:reverse]',
            )}
          >
            {[...column, ...column, ...column].map((card, at) => (
              <FeedCard key={at} {...card} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One opening, straight off the inspector: a 16px-radius panel of #37124A at
 * half strength with a 1px glint along its top edge and 16px of padding; a
 * 28px lilac monogram; the company in 11px tracked caps at 55%, the role in
 * Inter Tight semibold, the place and pay at 55% again.
 */
function FeedCard({ company, role, place, pay, tags }) {
  return (
    <article className="rounded-2xl border-t border-[#EFE6F7]/16 bg-[#37124A]/50 p-4 transition-colors duration-500 hover:bg-[#37124A]/80">
      <p className="flex items-center gap-3">
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-[#B98BFF] text-sm font-semibold text-[#3B1466]">
          {company[0]}
        </span>
        <span className="text-[0.6875rem] leading-normal tracking-[1px] text-white/55 uppercase">
          {company}
        </span>
      </p>
      <h3 className="font-tight text-on-night mt-3 text-[0.9375rem] leading-tight font-semibold">
        {role}
      </h3>
      <p className="mt-1 text-xs leading-normal text-white/55">
        {place} · {pay}
      </p>
      <p className="mt-3 flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-white/15 px-2.5 py-1 text-[0.6875rem] leading-none text-white/70"
          >
            {tag}
          </span>
        ))}
      </p>
    </article>
  );
}

/**
 * How far an unlit step is knocked back: the lit step's neighbours to one
 * level, anything further to a lower one, so the row fades away from the
 * step that is lit.
 */
const INACTIVE = { near: 0.3, far: 0.15 };
const dimness = (distance) => (distance === 0 ? 1 : distance === 1 ? INACTIVE.near : INACTIVE.far);

/** Seconds a step takes to light once the row has settled on it, and to let go. */
const LIGHT = 0.25;
const RELEASE = 0.2;

/**
 * The settle, in two beats: the row is thrown past the step by a fixed
 * share of a card pitch, then springs back onto it -- past, back, past a
 * little less, and to rest. A fixed overshoot rather than an elastic ease
 * on the whole move, because an ease overshoots in proportion to the
 * distance, and a short gesture would barely wobble.
 * `elastic.out(amplitude, period)`: a bigger amplitude swings wider, a
 * shorter period bounces more times.
 */
const SETTLE = {
  overshoot: 0.1,
  out: { duration: 0.3, ease: 'power2.out' },
  // A short period for many small swings, over a long enough duration that
  // they die away slowly -- a jelly wobbling itself still, not a snap.
  back: { duration: 2, ease: 'elastic.out(1, 0.3)' },
  // Seconds into the spring-back at which the step lights: on the first
  // return past the step, not once the last wobble has died away.
  lightAt: 0.25,
  // A gesture is over when the scrollbar has stopped and snapped, however
  // long its scroll events trickle on for; a flick of several notches is
  // still one step. This is only the backstop, for a scroll that stops
  // dead on a step and gives the snap nothing to do.
  gesture: 2.5,
};

/**
 * The scroll snap under the settle. Short: it only has to carry the
 * scrollbar to the step's position while the row does the visible work.
 */
const SNAP = { delay: 0.05, duration: { min: 0.3, max: 0.5 } };

/**
 * The steps, as a stepper: one gesture, one step.
 *
 * The section pins for one viewport per handover, but the reader is never
 * scrubbing the row. The first movement of the wheel is the gesture: the
 * lit step lets go and the row is thrown at the next step in that direction
 * on a spring -- past it, back, and to rest -- lighting it on the way back.
 * The scroll then snaps one step the same way underneath, so the scrollbar
 * agrees with the row. The lit stretch of the rail rides the same spring.
 *
 * The heading and its copy are on their own once-only timeline: they have
 * arrived before the pin engages, and scrubbing them would mean dragging the
 * wheel to finish reading a sentence.
 */
function HowItWorks() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const scope = root.current;
    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(scope);

      const entrance = gsap.timeline({ paused: true });
      const reveal = makeReveal(entrance, select);
      reveal('.js-hw-line', HEADLINE_FROM, HEADLINE_TO);
      reveal(
        '.js-hw-copy',
        { y: 20, opacity: 0 },
        { duration: 0.8, stagger: 0.12, ease: 'power3.out' },
        1.2,
      );
      reveal(
        '.js-step',
        { y: 28, opacity: 0 },
        { duration: 0.9, stagger: 0.1, ease: 'power3.out' },
        1.5,
      );
      playWhenSeen(scope, entrance, { start: 'top 75%', once: true });

      // Everything below is scrubbed. `fromTo` on every tween, so the start
      // state is written the moment the timeline is built and the section
      // renders with step one active before the reader reaches it.
      const numbers = select('.js-step-number');
      const cards = select('.js-step-card');
      const fills = select('.js-step-fill');
      const titles = select('.js-step-title');
      const panels = select('.js-step-panel');
      const bodies = select('.js-step-body');
      const markers = select('.js-step-marker');
      const steps = select('.js-step');
      const [track] = select('.js-track');
      const last = STEPS.length - 1;

      // The row is wider than the screen and slides so a step can sit at the
      // centre of it, as the frame lays it out. Measured, not fixed: where a
      // step's centre is depends on the card width and the gap, both of
      // which change with the viewport. Function-valued so a refresh -- a
      // resize, the fonts landing -- re-measures rather than replays stale
      // pixels.
      const centreOn = (index) => () =>
        track.parentElement.clientWidth / 2 -
        (steps[index].offsetLeft + steps[index].offsetWidth / 2);

      // -- lighting ---------------------------------------------------------
      // Not on the scrubbed timeline: a lit step is a state the row is in
      // when it is at rest, not a point on the scrollbar.
      let lit = 0;

      const release = () => {
        if (lit === null) return;
        const index = lit;
        lit = null;
        gsap.to([numbers[index], cards[index]], { opacity: INACTIVE.near, duration: RELEASE });
        gsap.to([fills[index], markers[index]], { opacity: 0, duration: RELEASE });
      };

      // Every step is re-graded from the lit one outwards, not just the
      // one that lights.
      const light = (index) => {
        if (lit === index) return;
        release();
        lit = index;
        STEPS.forEach((_, i) => {
          const distance = Math.abs(i - index);
          gsap.to([numbers[i], cards[i]], { opacity: dimness(distance), duration: LIGHT });
          gsap.to([fills[i], markers[i]], { opacity: distance === 0 ? 1 : 0, duration: LIGHT });
        });
        // The card is written in as it lights, on the page's own terms: the
        // card lifts into place, the title rises out of its mask and comes
        // into focus, the box wipes open from its top edge, and the copy is
        // written out from the left inside it. All transient: everything
        // ends where it rests, so the rows stay in line.
        gsap
          .timeline({ defaults: { ease: 'power3.out', overwrite: 'auto' } })
          .fromTo(cards[index], { scale: 0.96, y: 8 }, { scale: 1, y: 0, duration: 0.6 }, 0)
          .fromTo(
            titles[index],
            { yPercent: 110, scale: 0.97, filter: 'blur(6px)' },
            { yPercent: 0, scale: 1, filter: 'blur(0px)', duration: 0.7 },
            0.05,
          )
          .fromTo(
            panels[index],
            { clipPath: 'inset(0 0 100% 0)', y: 10 },
            { clipPath: 'inset(0 0 0% 0)', y: 0, duration: 0.6 },
            0.12,
          )
          .fromTo(
            bodies[index],
            { clipPath: 'inset(0 100% 0 0)', x: -14, opacity: 0, filter: 'blur(4px)' },
            { clipPath: 'inset(0 0% 0 0)', x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.7 },
            0.3,
          );
      };

      // -- the row ----------------------------------------------------------
      const [rail] = select('.js-progress');
      const shown = { progress: 0 };
      const showRail = () => {
        rail.style.clipPath = `inset(0 ${(1 - shown.progress) * 100}% 0 0)`;
      };

      // The row is thrown past a step and springs back onto it, lighting
      // it on the way back. The rail rides the same two beats, in its own
      // units.
      const pitch = () => steps[1].offsetLeft - steps[0].offsetLeft;
      // The step the row was last sent to. Direction is judged against it,
      // never against where the row happens to be mid-bounce.
      let aimed = 0;
      let travelling = false;
      // The settle under way, killed whole by the next one: its later beats
      // -- the light, the end of the gesture -- are for the step it was
      // thrown at, and must not fire into the throw that replaced it.
      let travel;
      const settle = (index) => {
        // Already there, or already on the way: a snap that lands on the
        // pin's very end also reports a leave, and that must not throw the
        // row at the step it is settling on.
        if (index === aimed) return;
        travel?.kill();
        const forward = index >= aimed;
        aimed = index;
        travelling = true;
        const target = centreOn(index)();
        const beyond = target + (forward ? -1 : 1) * pitch() * SETTLE.overshoot;
        const railTarget = index / last;
        const railBeyond = railTarget + ((forward ? 1 : -1) * SETTLE.overshoot) / last;

        travel = gsap
          .timeline({ defaults: { force3D: true } })
          .to(track, { x: beyond, ...SETTLE.out })
          .to(track, { x: target, ...SETTLE.back })
          .to(shown, { progress: railBeyond, onUpdate: showRail, ...SETTLE.out }, 0)
          .to(
            shown,
            { progress: railTarget, onUpdate: showRail, ...SETTLE.back },
            SETTLE.out.duration,
          )
          .call(() => light(index), null, SETTLE.out.duration + SETTLE.lightAt)
          .call(
            () => {
              travelling = false;
            },
            null,
            SETTLE.gesture,
          );
      };

      const clampStep = gsap.utils.clamp(0, last);
      let snapping = false;

      const pin = ScrollTrigger.create({
        trigger: scope,
        start: 'top top',
        end: `+=${last * 100}%`,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        // The first movement of the wheel *is* the gesture: the row lets go
        // and is thrown at the next step in that direction there and then,
        // rather than trailing the scrollbar and waiting for it to stop.
        // The scroll snap below brings the scrollbar to the same step
        // underneath. Not while the snap is scrolling, and not while the row
        // is already on its way: one gesture, one step.
        onUpdate: (self) => {
          const velocity = self.getVelocity();
          if (snapping || travelling) return;
          if (Math.abs(velocity) < 20) return;
          release();
          settle(clampStep(aimed + Math.sign(velocity)));
        },
        // The scrollbar follows the row: one step per gesture, in the
        // direction of travel, however small the gesture. `inertia` off, or
        // a hard flick would be projected forward and skip steps.
        snap: {
          snapTo: 1 / last,
          directional: true,
          inertia: false,
          delay: SNAP.delay,
          duration: SNAP.duration,
          ease: 'power2.inOut',
          onStart: () => {
            snapping = true;
          },
          // If the scrollbar landed on a different step from the one the
          // row was thrown at -- a long drag, say -- send the row there.
          // The scrollbar has stopped and snapped: the gesture is over,
          // whatever the row is still doing.
          onComplete: (self) => {
            snapping = false;
            travelling = false;
            const landed = Math.round(self.progress * last);
            if (landed !== aimed) settle(landed);
          },
          onInterrupt: () => {
            snapping = false;
          },
        },
        // Leaving the pin either way is a stop on an end step -- and the
        // end of the gesture, since out here there is no snap to end it.
        // (Not on entering: that fires after the same update's `onUpdate`,
        // and would free the row right behind its first throw.)
        onLeave: () => {
          settle(last);
          travelling = false;
        },
        onLeaveBack: () => {
          settle(0);
          travelling = false;
        },
        onRefresh: () => {
          if (!travelling) {
            gsap.set(track, { x: centreOn(aimed), force3D: true });
            shown.progress = aimed / last;
            showRail();
          }
        },
      });

      // Once the pin lets go, the heading grows and dissolves over the
      // section's own height of scroll, as the home hero does. Driven from
      // the pin's end, since the section's own layout position stays at
      // the pin's start for the whole pin.
      zoomOutOnScroll(
        gsap.timeline({
          scrollTrigger: {
            trigger: scope,
            start: () => pin.end,
            end: () => pin.end + scope.offsetHeight,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }),
        select('.js-hw-head'),
      );
    });

    return () => media.revert();
  }, []);

  return (
    <section
      ref={root}
      className="bg-night short:pt-20 short:pb-8 relative flex min-h-dvh flex-col justify-center overflow-hidden py-20"
    >
      <Glow className="top-0 -left-40" size={640} opacity={0.12} />
      <Glow className="top-[45%] -left-40" size={440} tone="white" opacity={0.16} blur={90} />
      <Glow className="-right-32 bottom-0" size={520} tone="lilac" opacity={0.1} />

      <div>
        <div className="wonder-container wonder-measure relative">
          <div className="js-hw-head mx-auto max-w-3xl text-center">
            <h2 className="font-tight text-[clamp(2rem,3.15vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.033em]">
              {/* Two segments behind their own masks, as the home hero's
                  "Internships" and "made easy.": "How Wonder" rises, and
                  "Works" follows it 0.6s later. */}
              <span className="inline-block overflow-hidden pb-[0.1em] mb-[-0.1em] align-bottom">
                <span className="js-hw-line text-on-night block">How Wonder</span>
              </span>{' '}
              <span className="inline-block overflow-hidden pb-[0.1em] mb-[-0.1em] align-bottom">
                <span className="js-hw-line text-iris-300 block">Works</span>
              </span>
            </h2>
            <p className="js-hw-copy text-on-night short:mt-4 mt-6 text-xl font-medium">
              From Profile To Perfect Match
            </p>
            <p className="js-hw-copy text-on-night-muted short:mt-2 mt-4">
              Wonder uses AI to understand what students want and what startups need.
            </p>
            <div className="js-hw-copy short:mt-5 mt-8">
              <LinkButton
                to={paths.registerStudent}
                variant="outlineLight"
                shape="pill"
                size="pillMd"
              >
                Get Started
                <Icon name="arrowRight" size={16} />
              </LinkButton>
            </div>
          </div>
        </div>
      </div>

      {/* The row lives outside the measure: it is wider than the screen on
          purpose and the timeline slides it. The card width and the gap are
          custom properties so the progress line can be sized off the same two
          numbers in CSS instead of being measured. */}
      <div className="short:mt-8 relative mt-16 w-full lg:mt-20 lg:short:mt-8">
        <ol
          className={cn(
            'js-track relative flex will-change-transform',
            '[--card:min(26rem,85vw)] [--gap:clamp(3rem,6.25vw,7.5rem)] gap-(--gap)',
          )}
          style={{ '--span': STEPS.length - 1 }}
        >
          {/* The dashed rail runs the full width of the row, from the first
              card's left edge to the last card's right, through the vertical
              centre of the numbers. The solid line that grows along it spans
              only number to number: half a card in, one pitch per handover
              long. */}
          <div
            aria-hidden="true"
            className="border-iris-300/40 short:top-12 pointer-events-none absolute top-15 left-0 w-[calc(var(--span)*(var(--card)+var(--gap))+var(--card))] border-t border-dashed"
          />
          {/* The lit stretch of the rail: the same dashes in the accent
              colour, marching towards the next step, over a strip of ground
              so the resting dashes underneath do not show through the gaps.
              Only the part the reader has reached is revealed. */}
          <div
            aria-hidden="true"
            style={{ clipPath: 'inset(0 100% 0 0)' }}
            className="js-progress bg-night short:top-12 pointer-events-none absolute top-15 left-[calc(var(--card)/2)] h-0.5 w-[calc(var(--span)*(var(--card)+var(--gap)))] -translate-y-px bg-[repeating-linear-gradient(90deg,var(--color-iris-300)_0_8px,transparent_8px_16px)] bg-size-[16px_2px] motion-safe:animate-[wonder-march_0.9s_linear_infinite]"
          />

          {STEPS.map((step, index) => (
            <li
              key={step.title}
              className="js-step relative flex w-(--card) shrink-0 flex-col items-center"
            >
              {/* Two spans: the outer one is solid ground that breaks the
                  rail either side of the figure, the inner one is what fades.
                  On one element the ground faded with the number and the rail
                  showed through the inactive ones. */}
              <span className="bg-night relative z-10 px-8">
                <span
                  className={cn(
                    'js-step-number font-tight text-on-night short:text-[6rem] block text-[7.5rem] leading-none font-semibold',
                    index === 1 && 'opacity-30',
                    index > 1 && 'opacity-15',
                  )}
                >
                  {index + 1}
                </span>
              </span>

              {/* Inspector: the card is #CDACF6 at 20% under a 24px blur, 22px
                  radius, 1px white at 8%; the panel inside it #28003D, 1px
                  white at 20%, the same blur. Title Inter Tight 600 22 on 36,
                  body Inter Tight 400 18 on 26 at 70%. */}
              <div
                className={cn(
                  'js-step-card short:mt-8 relative isolate mt-10 w-full rounded-[22px] border border-white/6 bg-white/4 p-3 pt-8 text-center',
                  index === 1 && 'opacity-30',
                  index > 1 && 'opacity-15',
                )}
              >
                {/* The active surface, a layer over the resting one, faded
                    on as a step lights and off as it lets go. No backdrop
                    blur here: a blur re-sampled every frame while the row
                    moves is what shimmered during the glide, and over the
                    flat night ground it was invisible anyway. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'js-step-fill absolute inset-0 -z-10 rounded-[22px] border border-white/8 bg-[#CDACF6]/20',
                    index > 0 && 'opacity-0',
                  )}
                />
                {/* A white ring on every step; only the lit one is filled. */}
                <span
                  className="absolute -top-3.5 left-1/2 size-7 -translate-x-1/2 rounded-full border-[3px] border-white"
                  aria-hidden="true"
                >
                  <span
                    className={cn(
                      'js-step-marker bg-iris-700 absolute inset-0 rounded-full',
                      index > 0 && 'opacity-0',
                    )}
                  />
                </span>
                {/* The title rises out of a mask as the step lights, the way
                    every heading on the page arrives. */}
                <h3 className="font-tight text-on-night overflow-hidden text-[1.375rem] leading-9 font-semibold">
                  <span className="js-step-title block">{step.title}</span>
                </h3>
                <p className="js-step-panel font-tight short:mt-3 short:min-h-32 short:py-5 mt-4 min-h-38.5 rounded-2xl border border-white/20 bg-[#28003D] px-6 py-7 text-lg leading-[1.444] text-white/70 backdrop-blur-xl">
                  <span className="js-step-body block">{step.body}</span>
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
